import { useMutation } from '@apollo/client';
import EditIcon from '@mui/icons-material/Edit';
import {
  Alert,
  Autocomplete,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormGroup,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DateRange, DateRangePicker } from '@mui/x-date-pickers-pro';
import { FormSubmitRow } from 'components/common/form/submit-row';
import { TooltipIconButton } from 'components/common/icon/tooltip-icon-button';
import {
  CreateSchedulerFlowExperimentMutation,
  SchedulerFlowExperimentMutation,
} from 'components/views/campaign/outreach-template-experiments/outreach-template-experiments.graphql';
import { OutreachTemplatesContext } from 'components/views/campaign/outreach-template-experiments/outreach-templates-context-props';
import { getSourceLlmConfig } from 'components/views/company/outreach-template/duplicate-outreach-template.util';
import { FormCheckbox } from 'components/views/forms/form-checkbox';
import { FormTextField } from 'components/views/forms/form-text-field';
import moment from 'moment';
import { useDemoMode } from 'providers/demo-mode';
import { FC, FormEvent, useContext, useMemo, useState } from 'react';
import { Controller, FormProvider, useForm, useFormContext, useWatch } from 'react-hook-form';
import {
  CreateSchedulerFlowExperiment,
  CreateSchedulerFlowExperimentVariables,
} from 'types/apollo/CreateSchedulerFlowExperiment';
import { UpdateSchedulerFlowExperimentInput } from 'types/apollo/globalTypes';
import { SchedulerFlowExperiments_schedulerFlowExperiments } from 'types/apollo/SchedulerFlowExperiments';
import { SchedulerFlowsForExperiments_schedulerFlowsByCompanyId } from 'types/apollo/SchedulerFlowsForExperiments';
import {
  UpdateSchedulerFlowExperiment,
  UpdateSchedulerFlowExperimentVariables,
} from 'types/apollo/UpdateSchedulerFlowExperiment';

export interface Data {
  schedulerFlowAId: string | null;
  schedulerFlowBId: string | null;
  schedulerFlowCId: string | null;
  schedulerFlowDId: string | null;
  schedulerFlowAVolume: number | null;
  schedulerFlowBVolume: number | null;
  schedulerFlowCVolume: number | null;
  schedulerFlowDVolume: number | null;
  useDateRange: boolean;
  dateRange: DateRange<Date>;
}

type SchedulerFlow = {
  id: number;
  name: string;
};

type BaseVolumeProps = 'schedulerFlowA' | 'schedulerFlowB' | 'schedulerFlowC' | 'schedulerFlowD';
const schedulerFlowProps: (keyof Data)[] = [
  'schedulerFlowAId',
  'schedulerFlowBId',
  'schedulerFlowCId',
  'schedulerFlowDId',
];
type OutreachTemplateType = {
  [K in BaseVolumeProps]: SchedulerFlow;
};

/** An LLM template without its config can't serve a running campaign's leads. */
export const missingLlmConfig = (sf: SchedulerFlowsForExperiments_schedulerFlowsByCompanyId) =>
  getSourceLlmConfig(sf)?.kind === 'none';

export const ExperimentPicker: FC<{ schedulerFlow: keyof OutreachTemplateType; label: string }> = ({
  schedulerFlow,
  label,
}) => {
  const { getRandomCampaignName } = useDemoMode();
  const form = useFormContext<Data>();
  const { options, campaignRunning } = useContext(OutreachTemplatesContext);
  const blocked = (sf: SchedulerFlowsForExperiments_schedulerFlowsByCompanyId) =>
    campaignRunning && missingLlmConfig(sf);
  const [value, setValue] = useState<SchedulerFlowsForExperiments_schedulerFlowsByCompanyId | null>(
    options.find((x) => x.id == form.getValues(`${schedulerFlow}Id`)) ?? null,
  );
  return (
    <Stack direction="row" spacing={1} sx={{ padding: '6px' }}>
      <Autocomplete
        value={value}
        options={options}
        openOnFocus
        sx={{ width: 350 }}
        onChange={(_, newValue: SchedulerFlowsForExperiments_schedulerFlowsByCompanyId | null) => {
          setValue(newValue);
          form.setValue(`${schedulerFlow}Id`, newValue?.id ?? null);
        }}
        getOptionLabel={(sf) => {
          const prefix = blocked(sf) ? '(no LLM config) ' : '';
          return `${prefix}${sf.id} - ${getRandomCampaignName(sf.name ?? undefined, '', sf.id)}`;
        }}
        getOptionDisabled={(sf) => {
          const values = form.getValues(schedulerFlowProps);
          return (values.includes(sf.id) && value?.id != sf.id) || blocked(sf);
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            size="medium"
            label={label}
            InputProps={{
              ...params.InputProps,
            }}
          />
        )}
      />
      {value != null && (
        <FormTextField
          form={form}
          label="Volume"
          name={`${schedulerFlow}Volume`}
          maxLength={3}
          minLength={1}
          type="number"
          sx={{ width: 125 }}
          InputProps={{
            endAdornment: <InputAdornment position="end">%</InputAdornment>,
          }}
          inputProps={{
            min: 0,
            max: 100,
            inputMode: 'numeric',
            pattern: '[0-9]*',
          }}
        ></FormTextField>
      )}
    </Stack>
  );
};

const isDateValid = (date: Date) => !isNaN(date.getTime());

export const CreateExperiment: FC = () => {
  const form = useFormContext<Data>();
  const { options, campaignRunning } = useContext(OutreachTemplatesContext);

  const handleDateChange = (date: DateRange<Date> | null) => {
    if (!date) return;

    form.setValue('dateRange', date);
  };

  return (
    <FormGroup>
      <Stack direction="column" spacing={2}>
        {campaignRunning && options.some(missingLlmConfig) && (
          <Alert severity="warning">
            Outreach templates with no LLM configuration can&apos;t be used while this campaign is
            live or draining.
          </Alert>
        )}
        <ExperimentPicker label="A test" schedulerFlow="schedulerFlowA" />
        <ExperimentPicker label="B test" schedulerFlow="schedulerFlowB" />
        <ExperimentPicker label="C test" schedulerFlow="schedulerFlowC" />
        <ExperimentPicker label="D test" schedulerFlow="schedulerFlowD" />

        <FormCheckbox
          form={form}
          name="useDateRange"
          label="Use date range"
          sx={{ width: 'fit-content' }}
        />

        <Controller
          control={form.control}
          name="dateRange"
          render={({ field }) => (
            <DateRangePicker
              localeText={{ start: 'Start date', end: 'End date' }}
              calendars={2}
              timezone="UTC"
              disabled={!form.watch('useDateRange')}
              value={field.value}
              format="MM/dd/yyyy"
              onChange={(v) => {
                handleDateChange(v);
                field.onChange(v);
              }}
            />
          )}
        />
      </Stack>
    </FormGroup>
  );
};

export type CreateOrEditExperimentProps = Pick<
  SchedulerFlowExperiments_schedulerFlowExperiments,
  | 'id'
  | 'schedulerFlowAId'
  | 'schedulerFlowBId'
  | 'schedulerFlowCId'
  | 'schedulerFlowDId'
  | 'schedulerFlowAVolume'
  | 'schedulerFlowBVolume'
  | 'schedulerFlowCVolume'
  | 'schedulerFlowDVolume'
  | 'start'
  | 'end'
>;
const creationDefaultParams: Data = {
  schedulerFlowAId: null,
  schedulerFlowBId: null,
  schedulerFlowCId: null,
  schedulerFlowDId: null,
  schedulerFlowAVolume: null,
  schedulerFlowBVolume: null,
  schedulerFlowCVolume: null,
  schedulerFlowDVolume: null,
  dateRange: [new Date(), null],
  useDateRange: false,
};

const adjustDate = (date: Date | null) => {
  if (date == null) {
    return null;
  }
  return moment(date).toDate();
};

export const ExperimentForm: FC<{
  data: CreateOrEditExperimentProps | null;
  campaignId?: string;
  refetch: () => Promise<any>;
  handleClose: () => void;
}> = ({ data, campaignId, refetch, handleClose }) => {
  const [submitting, setSubmitting] = useState(false);
  const form = useForm<Data>({
    defaultValues:
      data == null
        ? { ...creationDefaultParams }
        : {
            schedulerFlowAId: data.schedulerFlowAId,
            schedulerFlowBId: data.schedulerFlowBId,
            schedulerFlowCId: data.schedulerFlowCId,
            schedulerFlowDId: data.schedulerFlowDId,
            schedulerFlowAVolume: data.schedulerFlowAVolume ?? 0,
            schedulerFlowBVolume: data.schedulerFlowBVolume ?? 0,
            schedulerFlowCVolume: data.schedulerFlowCVolume ?? 0,
            schedulerFlowDVolume: data.schedulerFlowDVolume ?? 0,
            dateRange: [adjustDate(data.start), adjustDate(data.end)],
            useDateRange: data.start != null || data.end != null,
          },
    mode: 'all',
  });

  const formValues = useWatch({ control: form.control });

  const [updateSchedulerFlowExperiment] = useMutation<
    UpdateSchedulerFlowExperiment,
    UpdateSchedulerFlowExperimentVariables
  >(SchedulerFlowExperimentMutation);

  const [createSchedulerFlowExperiment] = useMutation<
    CreateSchedulerFlowExperiment,
    CreateSchedulerFlowExperimentVariables
  >(CreateSchedulerFlowExperimentMutation);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    try {
      setSubmitting(true);
      const fv = form.getValues();
      if (!fv.schedulerFlowAId || !fv.schedulerFlowAVolume) {
        return;
      }
      const params: Omit<UpdateSchedulerFlowExperimentInput, 'id'> = {
        schedulerFlowAId: fv.schedulerFlowAId,
        schedulerFlowBId: fv.schedulerFlowBId,
        schedulerFlowCId: fv.schedulerFlowCId,
        schedulerFlowDId: fv.schedulerFlowDId,
        schedulerFlowAVolume: Number(fv.schedulerFlowAVolume),
        schedulerFlowBVolume: fv.schedulerFlowBId ? Number(fv.schedulerFlowBVolume) : null,
        schedulerFlowCVolume: fv.schedulerFlowCId ? Number(fv.schedulerFlowCVolume) : null,
        schedulerFlowDVolume: fv.schedulerFlowDId ? Number(fv.schedulerFlowDVolume) : null,
        start:
          fv.useDateRange && fv.dateRange[0] ? moment.utc(fv.dateRange[0]).startOf('day') : null,
        end: fv.useDateRange && fv.dateRange[1] ? moment.utc(fv.dateRange[1]).endOf('day') : null,
      };

      if (data) {
        await updateSchedulerFlowExperiment({
          variables: {
            input: {
              id: data.id,
              ...params,
            },
          },
        });
      } else if (campaignId) {
        await createSchedulerFlowExperiment({
          variables: {
            input: {
              campaignId,
              ...params,
            },
          },
        });
      }

      await refetch();
      handleClose();
    } finally {
      setSubmitting(false);
    }
  };

  const sum = useMemo(() => {
    const fv = form.getValues();
    return schedulerFlowProps.reduce((prev, keyId) => {
      const value = Number(fv[keyId.replace('Id', 'Volume')] ?? 0);
      return prev + (fv[keyId] ? value : 0);
    }, 0);
  }, [formValues]);

  const invalidDateRange = useMemo(() => {
    const [useDateRange, dateRange] = form.getValues(['useDateRange', 'dateRange']);
    if (!useDateRange) {
      return false;
    }
    const [before, after] = dateRange;
    if (before == null || after == null) {
      return false;
    }
    return before > after;
  }, [formValues.useDateRange, formValues.dateRange]);

  const invalidDates = useMemo(() => {
    const [useDateRange, dateRange] = form.getValues(['useDateRange', 'dateRange']);
    if (!useDateRange) {
      return false;
    }
    const [before, after] = dateRange;
    return !((before == null || isDateValid(before)) && (after == null || isDateValid(after)));
  }, [formValues.useDateRange, formValues.dateRange]);

  return (
    <form onSubmit={handleSubmit}>
      <FormProvider {...form}>
        <DialogTitle>{data == null ? 'Create experiment' : 'Edit experiment'}</DialogTitle>

        <DialogContent>
          <CreateExperiment />
          <FormSubmitRow
            disabled={sum != 100 || invalidDateRange || invalidDates}
            onCancel={handleClose}
            loading={submitting}
            submitText={data == null ? 'Create' : 'Save'}
            cancelAlwaysEnabled
          >
            <Stack direction="column" spacing={1}>
              {sum != 100 && (
                <Typography color="error" sx={{ marginTop: 1 }}>
                  Sum must be 100% but it&apos;s {sum}%
                </Typography>
              )}
            </Stack>
          </FormSubmitRow>
        </DialogContent>
      </FormProvider>
    </form>
  );
};

export const CreateOrEditExperimentDialog: FC<{
  data: CreateOrEditExperimentProps | null;
  campaignId?: string;
  refetch: () => Promise<any>;
}> = ({ data, campaignId, refetch }) => {
  const [open, setOpen] = useState(false);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <div>
      {data == null ? (
        <Button variant="contained" size="small" onClick={handleClickOpen}>
          Create experiment
        </Button>
      ) : (
        <TooltipIconButton
          tooltip="Edit experiment"
          onClick={handleClickOpen}
          iconButtonProps={{ size: 'small', sx: { color: 'text.primary' } }}
        >
          <EditIcon fontSize="small" />
        </TooltipIconButton>
      )}
      <Dialog open={open} fullWidth onClose={handleClose}>
        <ExperimentForm
          data={data}
          refetch={refetch}
          campaignId={campaignId}
          handleClose={handleClose}
        />
      </Dialog>
    </div>
  );
};
